import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Crop, Gauge, History, Layers, LayoutGrid, LogOut, User } from 'lucide-react';
import { ShellContext, ToastHost, navItemActive, type NavItem } from '@os/ui';
import { useOsAuth } from '@os/auth-client/react';
import { useAddons, useMe, useQueueCount } from '../services/api';
import { OS_URL } from '../app/auth';
import { mergeAddonNav } from '../addons/nav';
import { useBatch } from '../stores/batch';
import { APP_NAV } from './appNav';

export const BRAND = { mark: 'PH', name: 'Photo' };

const HOST_NAV: NavItem[] = [
  { group: 'Overview' },
  { to: '/', label: 'Home', icon: Gauge, end: true },
  { group: 'Work' },
  { to: '/editor', label: 'Editor', icon: Crop },
  { to: '/queue', label: 'Batch queue', icon: Layers, end: true, badge: 'queue' },
  { to: '/log', label: 'Optimiser log', icon: History, end: true },
  ...APP_NAV,
];

/**
 * The prototype's chrome: one 48px top bar — brand, the app's nav with an accent underline on the current screen and
 * the queue count next to Batch queue, then the OS identity on the right. No sidebar. `PageHeader` still reads the
 * brand from `ShellContext`; its mobile menu button is hidden because the nav itself scrolls on narrow screens.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { data } = useMe();
  const auth = useOsAuth();
  const addons = useAddons();
  const loc = useLocation();
  const staged = useBatch((s) => s.items.length);
  const live = useQueueCount();
  const nav = mergeAddonNav(HOST_NAV, addons.data?.data ?? []);
  const links = nav.filter((it): it is Extract<NavItem, { to: string }> => 'to' in it);
  const counts: Record<string, number> = { queue: staged + (live.data ?? 0) };
  const name = data?.user.display_name ?? '…';
  const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <ShellContext.Provider value={{ brand: BRAND, menuOpen: false, setMenuOpen: () => {} }}>
      <div className="ph-app">
        <header className="ph-top">
          <NavLink to="/" className="ph-top-brand">
            <span className="ph-top-mark">{BRAND.mark}</span>
            <span className="ph-top-name">{BRAND.name}</span>
          </NavLink>
          <nav className="ph-top-nav" aria-label="Main">
            {links.map((it) => {
              const active = navItemActive(it.to, loc, it.end);
              const count = it.badge ? counts[it.badge] ?? 0 : 0;
              return (
                <NavLink key={it.to} to={it.to} className={`ph-top-link${active ? ' is-active' : ''}`} aria-current={active ? 'page' : undefined}>
                  <it.icon size={15} strokeWidth={1.5} />
                  <span>{it.label}</span>
                  {count > 0 && <b className="ph-top-count">{count}</b>}
                </NavLink>
              );
            })}
          </nav>
          <UserMenu initials={initials} name={name} roles={data?.roles ?? []} osUrl={OS_URL} onLogout={() => auth.logout(OS_URL)} />
        </header>
        <main className="ph-main">{children}</main>
        <ToastHost />
      </div>
    </ShellContext.Provider>
  );
}

function UserMenu({ initials, name, roles, osUrl, onLogout }: { initials: string; name: string; roles: string[]; osUrl: string; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open]);
  const home = osUrl.replace(/\/$/, '');
  return (
    <div className="ph-top-user" ref={ref}>
      <span className="ph-top-sso">OS SSO</span>
      <button type="button" className="ph-top-avatar" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} title={`${name} · ${roles.join(', ')}`}>{initials}</button>
      {open && (
        <div role="menu" className="ph-top-menu tl-in">
          <div className="ph-top-menu-who"><b className="truncate">{name}</b><span className="truncate">{roles.join(', ') || 'no role'}</span></div>
          <a role="menuitem" href={home}><LayoutGrid size={15} strokeWidth={1.5} />OS launcher</a>
          <a role="menuitem" href={`${home}/account`}><User size={15} strokeWidth={1.5} />Account</a>
          <a role="menuitem" href={`${home}/`} onClick={(e) => { e.preventDefault(); onLogout(); }}><LogOut size={15} strokeWidth={1.5} />Sign out</a>
        </div>
      )}
    </div>
  );
}
